import { useTableActionContext } from '@aibees/elephant/context';
import { exportScheme } from '@aibees/elephant/context/table-action/config';
import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { useLabelGroupingStatus } from '@aibees/elephant/hooks/labels/use-label-grouping-status';
import { useGetCreditsAmountTypeOnActionPopup } from '@aibees/elephant/hooks/use-get-credits-amount-type-on-action-popup';
import { ROUTES } from '@aibees/elephant/router';
import { SearchType } from '@aibees/elephant/types';
import { LabelType } from '@aibees/elephant/types/label';
import { useMatchRoute } from '@aibees/shared/hooks';
import {
  ACCORDION_IDS,
  DIALOG_IDS,
  getAccordionIdWithType,
  useTableAccordionStore,
} from '@aibees/shared/store/table-accordion-store';
import { YupFormData } from '@aibees/shared/utils/yup';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback, useEffect, useMemo } from 'react';
import type { ExportDialogProps } from '../types';
import { useExportDefaultValues } from './use-export-default-values';
import { usePrepareDataForIntegrationSetting } from './use-prepare-data-for-integration-setting';

export function useExportDialog({
  defaultName: _defaultName,
}: Pick<ExportDialogProps, 'defaultName'> = {}) {
  // Context hooks
  const {
    ids,
    handleExport,
    exportDialogState,
    handleClose,
    templateDialogState,
    previousState,
    setPreviousState,
    setExportName,
    exportName,
    integrationConfig,
    effectiveSearchType,
  } = useTableActionContext();
  // Match the flow's effective search type (folds in the ActionButton override)
  // so the label picker / newly created labels follow the record, not just the
  // page route — consistent with the export grouping used by handleExport.
  const grouping = useLabelGroupingStatus(effectiveSearchType);

  // Other hooks
  const { params } = useSearchQueryFilter();
  const isCompanyPage = useMatchRoute(ROUTES.searchCompany);
  // Use the flow's effective search type (folds in the ActionButton override) so
  // sections follow the record, not just the page route.
  const isPeople = effectiveSearchType === SearchType.People;
  const workspace = useWorkspace();
  const integrationSetting = usePrepareDataForIntegrationSetting();
  const defaultValues = useExportDefaultValues();

  // Credit types
  const {
    aiCompany,
    aiPeople,
    aiUnsavedPeople,
    basicPeople,
    basicUnsavedPeople,
  } = useGetCreditsAmountTypeOnActionPopup();

  // Memoized values
  const showAiLookalike = useMemo(
    () =>
      aiCompany ||
      basicPeople ||
      aiPeople ||
      aiUnsavedPeople ||
      basicUnsavedPeople ||
      isPeople,
    [
      aiCompany,
      basicPeople,
      aiPeople,
      aiUnsavedPeople,
      basicUnsavedPeople,
      isPeople,
    ]
  );

  const headerTitle = useMemo(
    () =>
      integrationConfig
        ? `Export to ${integrationConfig?.name}`
        : `Export (${ids.length})`,
    [integrationConfig, ids.length]
  );

  const listDefaultValue = useMemo(
    () => (isCompanyPage ? 'Contact' : 'Account'),
    [isCompanyPage]
  );

  // Use table accordion store directly
  const {
    getDialogExpanded,
    setDialogExpanded,
    setInitialExpanded,
    clearDialog,
  } = useTableAccordionStore();

  const dialogId = DIALOG_IDS.EXPORT_DIALOG;
  const expandedAccordion = getDialogExpanded(dialogId);

  // Determine initial expanded accordion based on previousState and conditions
  const getInitialAccordionId = useCallback(() => {
    if (previousState === LabelType.LABEL) {
      return getAccordionIdWithType('LABEL');
    }
    if (previousState === LabelType.LIST) {
      return getAccordionIdWithType('LIST');
    }
    // if (integrationConfig) {
    //   return ACCORDION_IDS.INTEGRATION_EXPORT;
    // }
    return integrationConfig ? null : ACCORDION_IDS.CSV_EXPORT;
  }, [previousState, integrationConfig]);

  // Set initial expanded accordion when dialog opens
  useEffect(() => {
    if (exportDialogState.value) {
      setInitialExpanded(dialogId, getInitialAccordionId());
    }
  }, [
    exportDialogState.value,
    dialogId,
    setInitialExpanded,
    getInitialAccordionId,
  ]);

  // Clear dialog state when dialog closes
  useEffect(() => {
    if (!exportDialogState.value) {
      clearDialog(dialogId);
    }
  }, [exportDialogState.value, clearDialog, dialogId]);

  // Handlers
  const setExpandedAccordion = useCallback(
    (accordionId: string | null) => {
      setDialogExpanded(dialogId, accordionId);
    },
    [dialogId, setDialogExpanded]
  );

  const handleExportWithAdditionalData = useCallback(
    (
      data: YupFormData<typeof exportScheme>,
      e: {
        startProgress: () => void;
        endProgress: () => void;
        toggle: () => void;
      },
      settingTemplate: any
    ) => {
      handleExport(data, e, params, settingTemplate, integrationSetting);
    },
    [handleExport, params, integrationSetting]
  );

  const handleCloseIntegrationConnectDialog = useCallback(() => {
    handleClose('integrationConnect');
    exportDialogState.onTrue();
  }, [handleClose, exportDialogState]);

  return {
    // State
    exportDialogState,
    templateDialogState,
    ids,
    expandedAccordion,
    showAiLookalike,
    workspace,
    isCompanyPage,
    listDefaultValue,
    headerTitle,
    defaultValues,
    integrationSetting,
    params,
    setPreviousState,
    setExportName,
    exportName,
    integrationConfig,
    grouping,
    // Handlers
    handleExport,
    handleClose,
    handleExportWithAdditionalData,
    handleCloseIntegrationConnectDialog,
    setExpandedAccordion,

    // Computed values
    dialogId,
    hasIntegrationConfig: !!integrationConfig,
  };
}
